import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share, PlusSquare, X, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const KEY = "xc_homescreen_tip";
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone;
const onHome = () => /(^|\.)xcelio\.app$/.test(window.location.hostname) || new URLSearchParams(window.location.search).has("hstip");

// Once, on his first visit to xcelio.app: a gentle "Add Xcelio to your home screen" card.
export default function HomeScreenTip() {
  const [show, setShow] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    if (!user || !onHome() || standalone() || localStorage.getItem(KEY)) return;
    const onBip = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onBip);
    const t = setInterval(() => { if (!document.querySelector('[data-testid="welcome-overlay"], [role="dialog"]')) { setShow(true); clearInterval(t); } }, 4000);
    return () => { clearInterval(t); window.removeEventListener("beforeinstallprompt", onBip); };
  }, [user]);
  const close = () => { localStorage.setItem(KEY, "1"); setShow(false); };
  const install = async () => { prompt.prompt(); await prompt.userChoice.catch(() => {}); close(); };
  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.5 }}
          className="fixed inset-x-3 bottom-24 sm:bottom-6 sm:left-auto sm:right-6 sm:w-96 z-[60] rounded-3xl border border-white/10 bg-card/90 backdrop-blur-xl shadow-2xl p-5" data-testid="homescreen-tip">
          <button onClick={close} aria-label="Close" className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" data-testid="homescreen-tip-close"><X className="w-4 h-4" /></button>
          <div className="flex items-start gap-4">
            <img src="/brand/icon-192.png" alt="" className="w-12 h-12 rounded-2xl shrink-0" />
            <div className="pr-4">
              <p className="font-serif text-lg leading-snug">Keep Xcelio one tap away</p>
              <p className="text-sm text-muted-foreground mt-1">Add it to your home screen and it opens like an app.</p>
            </div>
          </div>
          {prompt ? (
            <button onClick={install} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-3 text-sm" data-testid="homescreen-tip-install"><Download className="w-4 h-4" /> Add to home screen</button>
          ) : isIOS() ? (
            <ol className="mt-4 space-y-2 text-sm" data-testid="homescreen-tip-ios-steps">
              <li className="flex items-center gap-2"><span className="text-muted-foreground">1.</span> Tap <Share className="w-4 h-4 text-primary" /> <span className="font-medium">Share</span> in Safari's toolbar</li>
              <li className="flex items-center gap-2"><span className="text-muted-foreground">2.</span> Choose <PlusSquare className="w-4 h-4 text-primary" /> <span className="font-medium">Add to Home Screen</span></li>
            </ol>
          ) : (
            <p className="mt-4 text-sm" data-testid="homescreen-tip-steps">Open your browser menu (⋮) and choose <span className="font-medium">Add to Home screen</span>.</p>
          )}
          <button onClick={close} className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground" data-testid="homescreen-tip-later">Maybe later</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
